import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const Products = () => {
  const { role } = useAuth();
  const isVendor = role === 'VENDOR';

  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [products, setProducts]         = useState([]);
  const [centres, setCentres]           = useState([]);   // centres available for selection
  const [vendors, setVendors]           = useState([]);
  const [categories, setCategories]     = useState([]);
  const [myVendorProfile, setMyVendorProfile] = useState(null); // logged-in vendor's profile
  const [loading, setLoading]           = useState(true);
  const [editMode, setEditMode]         = useState(false);
  const [selectedId, setSelectedId]     = useState(null);

  const [formData, setFormData] = useState({
    name:      '',
    category:  'OTHER',
    price:     '',
    stock:     '',
    centreIds: [],   // multi-centre array (replaces single centreId)
    vendorId:  '',
    image:     ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (role === 'SUPER_ADMIN' || role === 'ADMIN') {
        const [prodRes, tempRes, catRes, vendRes] = await Promise.all([
          api.get('/admin/products'),
          api.get('/admin/centres'),
          api.get('/admin/categories'),
          api.get('/admin/vendors'),
        ]);
        setProducts(prodRes.data.data   || []);
        setCentres(tempRes.data.data    || []);
        setCategories(catRes.data.data  || []);
        setVendors(vendRes.data.data    || []);

        const firstCat    = catRes.data.data?.[0]?.filterKey  || 'OTHER';
        const firstCentre = tempRes.data.data?.[0]?._id       || '';
        setFormData(prev => ({ ...prev, category: firstCat, centreIds: firstCentre ? [firstCentre] : [] }));

      } else if (isVendor) {
        // VENDOR: use public categories + dedicated vendor centres endpoint
        const [prodRes, profileRes, catRes, tempRes] = await Promise.allSettled([
          api.get('/admin/products'),        // scoped to vendor's products by backend
          api.get('/vendors/me'),            // logged-in vendor profile + _id
          api.get('/categories'),            // public endpoint, no ADMIN guard
          api.get('/vendors/centres'),       // returns only vendor's assigned centres (name, location)
        ]);

        if (prodRes.status === 'fulfilled')    setProducts(prodRes.value.data?.data || []);
        if (catRes.status === 'fulfilled')     setCategories(catRes.value.data?.data || []);
        if (tempRes.status === 'fulfilled')    setCentres(tempRes.value.data?.data || []);

        if (profileRes.status === 'fulfilled') {
          const profile = profileRes.value.data?.data;
          if (profile) {
            setMyVendorProfile(profile);
            setFormData(prev => ({
              ...prev,
              vendorId: profile._id,
              category: catRes.status === 'fulfilled'
                ? (catRes.value.data?.data?.[0]?.filterKey || 'OTHER')
                : 'OTHER',
            }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  }, [role, isVendor]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

const handleInputChange = (e) => {
  setFormData({ ...formData, [e.target.name]: e.target.value });
};

// Toggle a centre in the multi-select list
const toggleCentre = (id) => {
  setFormData(prev => ({
    ...prev,
    centreIds: prev.centreIds.includes(id)
      ? prev.centreIds.filter(t => t !== id)
      : [...prev.centreIds, id]
  }));
};

const handleSaveProduct = async () => {
  if (!formData.name || !formData.price || !formData.stock || formData.centreIds.length === 0) {
    alert('Please fill all required fields and select at least one centre.');
    return;
  }

  // Build payload — always send centreIds array; backend handles centreId + assignedCentres
  const payload = {
    ...formData,
    centreIds: formData.centreIds,
    // For VENDOR: vendorId is pre-set and locked; for ADMIN: take from dropdown
  };

  try {
    if (editMode) {
      await api.put(`/admin/products/${selectedId}`, payload);
      alert('✅ Product updated successfully!');
    } else {
      await api.post('/admin/products', payload);
      alert('✅ New product added successfully!');
    }
    fetchData();
    setIsModalOpen(false);
    resetForm();
  } catch (err) {
    console.error('Save failed', err);
    alert('❌ Error saving product: ' + (err.response?.data?.message || err.message));
  }
};

const handleEdit = (product) => {
  setEditMode(true);
  setSelectedId(product._id);
  // Rebuild centreIds from product's assignedCentres or fallback to single centreId
  const existingCentreIds = (product.assignedCentres?.length > 0)
    ? product.assignedCentres.map(t => t._id || t)
    : product.centreId ? [product.centreId?._id || product.centreId] : [];
  setFormData({
    name:      product.name,
    category:  product.category || categories[0]?.filterKey || 'OTHER',
    price:     product.price,
    stock:     product.stock,
    centreIds: existingCentreIds,
    vendorId:  isVendor ? myVendorProfile?._id || '' : (product.vendorId?._id || product.vendorId || ''),
    image:     product.image || ''
  });
  setIsModalOpen(true);
};

const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this product?")) return;
  try {
    await api.delete(`/admin/products/${id}`);
    alert("✅ Product deleted!");
    fetchData();
  } catch (err) {
    console.error("Delete failed", err);
  }
};

const resetForm = () => {
  setEditMode(false);
  setSelectedId(null);
  setFormData({
    name:      '',
    category:  categories[0]?.filterKey || 'OTHER',
    price:     '',
    stock:     '',
    centreIds: [],
    // Vendor: keep their ID locked; Admin: clear
    vendorId:  isVendor ? (myVendorProfile?._id || '') : '',
    image:     ''
  });
};

return (
  <div className="p-8 relative min-h-screen bg-[#FAF9F6]">
    <div className="flex justify-between items-center mb-8">
      <div>
        <h2 className="font-serif text-3xl font-bold text-[#2A2858]">Centre Inventory</h2>
        <p className="mt-1 text-stone-500 text-sm">Manage products, images (Base64), and stock levels</p>
      </div>
      <button
        onClick={() => { resetForm(); setIsModalOpen(true); }}
        className="bg-[#2A2858] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-[#14122B] transition-colors flex items-center gap-2"
      >
        <span className="material-symbols-outlined text-sm font-bold">add_box</span> Add Product
      </button>
    </div>

    {loading ? (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2A2858]"></div>
      </div>
    ) : (
      <div className="bg-white border border-[#E8B84B]/20 rounded-2xl overflow-hidden shadow-xl">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 text-xs">
              <th className="px-6 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Image</th>
              <th className="px-6 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Product Name</th>
              <th className="px-6 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Category</th>
              <th className="px-6 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Price</th>
              <th className="px-6 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Stock</th>
              <th className="px-6 py-4 font-bold text-[#2A2858] uppercase tracking-widest">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {products.map((item) => (
              <tr key={item._id} className="hover:bg-stone-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="w-14 h-14 bg-stone-100 rounded-lg overflow-hidden border border-stone-200">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <span className="material-symbols-outlined">image</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-stone-800">{item.name}</div>
                  <div className="text-[10px] text-stone-400 uppercase font-bold tracking-tighter flex flex-wrap gap-1 mt-0.5">
                    {(item.assignedCentres?.length > 0
                      ? item.assignedCentres
                      : item.centreId ? [item.centreId] : []
                    ).map((t, i) => (
                      <span key={i} className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">🛕 {t?.name || 'Centre'}</span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-stone-600 text-sm">{item.category}</td>
                <td className="px-6 py-4 font-bold text-[#2A2858]">₹{item.price}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${item.stock > 10 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {item.stock} Units
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-3">
                    <button onClick={() => handleEdit(item)} className="text-stone-400 hover:text-blue-500 transition-colors">
                      <span className="material-symbols-outlined text-xl">edit</span>
                    </button>
                    <button onClick={() => handleDelete(item._id)} className="text-stone-400 hover:text-red-500 transition-colors">
                      <span className="material-symbols-outlined text-xl">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Product Modal */}
    {isModalOpen && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm p-4">
        <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col">
          <div className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 px-8 py-5 flex justify-between items-center shrink-0">
            <h3 className="font-serif text-2xl font-bold text-[#2A2858]">
              {editMode ? 'Edit Product Details' : 'Add New Product'}
            </h3>
            <button onClick={() => setIsModalOpen(false)} className="text-stone-400 hover:text-red-500 transition-colors">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <form className="p-8 grid grid-cols-2 gap-6 overflow-y-auto flex-1 custom-scrollbar">
            <div className="col-span-2">
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Product Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#2A2858]/20 focus:border-[#2A2858] outline-none transition-all" />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Product Image (Cloudinary)</label>
              <div className="flex flex-col md:flex-row gap-6 items-center bg-stone-50 p-6 rounded-2xl border border-stone-200">
                {formData.image ? (
                  <div className="w-32 h-32 rounded-2xl overflow-hidden border-2 border-[#E8B84B]/30 shadow-lg relative group shrink-0">
                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-[8px] font-black uppercase tracking-widest text-center">Product<br />Preview</span>
                    </div>
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-2xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center bg-white shrink-0">
                    <span className="material-symbols-outlined text-stone-200 text-3xl mb-1">shopping_bag</span>
                    <span className="text-[8px] text-stone-400 font-black uppercase tracking-widest text-center">No Image<br />Uploaded</span>
                  </div>
                )}

                <div className="flex-1 space-y-4 text-center md:text-left">
                  <ImageUploader
                    buttonText={formData.image ? "Change Product Photo" : "Upload Product Photo"}
                    onUploadSuccess={(url) => setFormData({ ...formData, image: url })}
                    folder="products"
                  />
                  <div className="space-y-1">
                    <p className="text-[10px] text-stone-500 font-medium italic flex items-center justify-center md:justify-start gap-1">
                      <span className="material-symbols-outlined text-[12px]">cloud_done</span>
                      High-quality product images boost sales
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Price (₹)</label>
              <input type="number" name="price" value={formData.price} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#2A2858]/20 focus:border-[#2A2858] outline-none transition-all" />
            </div>

            <div>
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Stock Quantity</label>
              <input type="number" name="stock" value={formData.stock} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#2A2858]/20 focus:border-[#2A2858] outline-none transition-all" />
            </div>

            <div>
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Category</label>
              <select name="category" value={formData.category} onChange={handleInputChange} className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#2A2858]/20 focus:border-[#2A2858] outline-none transition-all bg-white text-stone-800">
                {categories.map(cat => (
                  <option key={cat._id} value={cat.filterKey}>{cat.name}</option>
                ))}
                {categories.length === 0 && <option value="OTHER">General / Other</option>}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Centre Association *</label>
              {/* Multi-centre checkbox list */}
              {centres.length === 0 ? (
                <p className="text-xs text-stone-400 italic">No centres available</p>
              ) : (
                <div className="border border-stone-200 rounded-xl overflow-hidden overflow-y-auto" style={{ maxHeight: 160 }}>
                  {centres.map(t => {
                    const tid = t._id || t;
                    const tname = t.name || 'Centre';
                    const checked = formData.centreIds.includes(tid);
                    return (
                      <label key={tid}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-stone-50 ${checked ? 'bg-amber-50' : ''}`}
                        style={{ borderBottom: '1px solid #f5f5f4' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCentre(tid)}
                          className="w-4 h-4 accent-[#2A2858] rounded"
                        />
                        <span className={`text-sm font-medium ${checked ? 'text-[#2A2858]' : 'text-stone-700'}`}>🛕 {tname}</span>
                        {t.location && <span className="text-xs text-stone-400 ml-auto">{t.location}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {formData.centreIds.length > 0 && (
                <p className="text-[10px] text-[#2A2858] font-bold mt-1">{formData.centreIds.length} centre{formData.centreIds.length > 1 ? 's' : ''} selected</p>
              )}
            </div>

            <div className="col-span-2 md:col-span-1">
              <label className="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-2">Vendor Assignment</label>
              {isVendor ? (
                // VENDOR: show own name as locked read-only
                <div className="flex items-center gap-3 border border-stone-200 rounded-xl px-4 py-3 bg-stone-50">
                  <span className="material-symbols-outlined text-[#2A2858] text-lg">storefront</span>
                  <div>
                    <p className="text-sm font-bold text-stone-800">{myVendorProfile?.shopName || 'My Vendor Shop'}</p>
                    <p className="text-[10px] text-stone-400 uppercase tracking-widest">Assigned automatically · cannot change</p>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <select
                    name="vendorId"
                    value={formData.vendorId}
                    onChange={handleInputChange}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#2A2858]/20 focus:border-[#2A2858] outline-none transition-all bg-white appearance-none pr-10"
                  >
                    <option value="">-- No Vendor Assigned --</option>
                    {vendors.map(v => (
                      <option key={v._id} value={v._id}>{v.userId?.name || v.shopName || v.name} ({v.type || 'Merchant'})</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-stone-400">
                    <span className="material-symbols-outlined text-[20px]">expand_more</span>
                  </div>
                </div>
              )}
            </div>

            <div className="col-span-2 pt-6 flex gap-4">
              <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 bg-stone-100 text-stone-600 font-bold py-3.5 rounded-xl hover:bg-stone-200 transition-all">Discard</button>
              <button type="button" onClick={handleSaveProduct} className="flex-1 bg-[#2A2858] text-white font-bold py-3.5 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">
                {editMode ? 'Update Product' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </div>
);
};

export default Products;